/**
 * RACKET-LAB-FRONT-1 — RacketLabLanding (Jun 2026).
 *
 * Public landing for the Racket Lab app at `/racket-lab`. Mirrors the
 * design vocabulary of `/apps` (TEAM OS Hub) and Questionnaires landing.
 * Provides login / register CTAs (register honors PUBLIC_REGISTRATION
 * gate via the existing /register flow) and a feature grid.
 *
 * No real signup / no real consent / no customer-facing checkout.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n";
import PublicLayout from "@/components/layout/PublicLayout";
import LanguageDropdown from "@/components/nav/LanguageDropdown";
import {
  Wrench, Inbox, CalendarDays, Boxes, ShieldAlert, ArrowRight, Sparkles,
  ClipboardList, Plus,
} from "lucide-react";

const FEATURES = [
  { key: "tickets", icon: Inbox, accent: "emerald" },
  { key: "calendar", icon: CalendarDays, accent: "indigo" },
  { key: "inventory", icon: Boxes, accent: "slate" },
  { key: "workforce", icon: ClipboardList, accent: "amber" },
  { key: "myGear", icon: Wrench, accent: "emerald" },
  { key: "storeAccess", icon: ShieldAlert, accent: "rose" },
];

const ACCENTS = {
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50/60", text: "text-emerald-700" },
  amber: { border: "border-amber-200", bg: "bg-amber-50/60", text: "text-amber-700" },
  indigo: { border: "border-indigo-200", bg: "bg-indigo-50/60", text: "text-indigo-700" },
  rose: { border: "border-rose-200", bg: "bg-rose-50/60", text: "text-rose-700" },
  slate: { border: "border-slate-200", bg: "bg-slate-50/60", text: "text-slate-700" },
};

const FeatureCard = ({ f, t }) => {
  const Icon = f.icon;
  const accent = ACCENTS[f.accent] || ACCENTS.emerald;
  return (
    <article
      data-testid={`rl-landing-feature-${f.key}`}
      className={`flex flex-col gap-2 rounded-2xl border ${accent.border} ${accent.bg} p-5 transition hover:shadow-md`}
    >
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl border bg-white/80 ${accent.border} ${accent.text}`}>
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-base font-semibold text-slate-900">{t(`racketLabLanding.features.${f.key}.title`)}</h3>
      <p className="text-xs text-slate-600">{t(`racketLabLanding.features.${f.key}.desc`)}</p>
    </article>
  );
};

const RacketLabLanding = () => {
  const { t } = useLang();

  return (
    <PublicLayout>
      <div data-testid="rl-landing-page" className="tsw-section py-12">
        <div className="mb-6 flex items-center justify-end" data-testid="rl-landing-lang-bar">
          <LanguageDropdown variant="navbar" testIdPrefix="rl-landing-lang" />
        </div>
        <header className="mb-10 max-w-3xl">
          <div className="tsw-overline mb-2 text-emerald-700">TEAM OS · Racket Lab</div>
          <h1 className="font-display text-4xl font-bold text-slate-900 sm:text-5xl lg:text-6xl" data-testid="rl-landing-title">
            {t("racketLabLanding.title")}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">{t("racketLabLanding.subtitle")}</p>
          <div className="mt-6 flex flex-wrap gap-3" data-testid="rl-landing-cta">
            <Link to="/login?next=/app/racket-lab" className="tsw-btn-primary inline-flex items-center gap-2" data-testid="rl-landing-cta-login">
              {t("common.login")} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/register" className="tsw-btn-secondary inline-flex items-center gap-2" data-testid="rl-landing-cta-register">
              {t("common.register")} <Plus className="h-4 w-4" />
            </Link>
            <Link to="/apps" className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100" data-testid="rl-landing-cta-hub">
              TEAM OS Hub <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <p className="mt-3 flex items-center gap-1 text-[11px] text-slate-500" data-testid="rl-landing-preview-note">
            <Sparkles className="h-3 w-3 text-emerald-700" />
            {t("racketLabLanding.previewNote")}
          </p>
        </header>

        <section className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="rl-landing-features-grid">
          {FEATURES.map((f) => (
            <FeatureCard key={f.key} f={f} t={t} />
          ))}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="rl-landing-roles">
          <h2 className="text-lg font-semibold text-slate-900">{t("racketLabLanding.rolesTitle")}</h2>
          <p className="mt-1 text-sm text-slate-600">{t("racketLabLanding.rolesIntro")}</p>
          <ul className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            {["admin", "staff", "qa_manager", "qa_tester", "shop_manager", "operator", "racket_technician"].map((r) => (
              <li key={r} data-testid={`rl-landing-role-${r}`} className="rounded border border-slate-200 bg-slate-50/50 p-2 text-xs text-slate-700">
                <strong className="text-slate-900">{t(`roles.${r}`) || r}</strong> · {t(`racketLabLanding.roleScope.${r}`)}
              </li>
            ))}
          </ul>
        </section>

        <footer className="mt-8 rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] italic text-slate-600" data-testid="rl-landing-footer-legal">
          {t("racketLabLanding.footerLegal")}
        </footer>
      </div>
    </PublicLayout>
  );
};

export default RacketLabLanding;
